import React, { useState, useEffect } from 'react';
import './Blog.css';
import { useComponentAnalytics } from '../hooks/useAnalytics';
import { useAuth } from '../hooks/useAuth';
import BlogPostForm from './BlogPostForm';
import { blogService } from '../services/blogService';

const Blog = ({ onNavigate }) => {
  const { trackInteraction } = useComponentAnalytics('Blog');
  const { isAuthenticated } = useAuth();
  const [selectedPost, setSelectedPost] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);

  const handlePostClick = (postId, postTitle) => {
    trackInteraction('post_click', 'blog_post', {
      post_id: postId,
      post_title: postTitle
    });
    // Encontrar el post completo y mostrarlo 
    const post = blogPosts.find(p => p.id === postId);
    setSelectedPost(post);
    // Actualizar URL con el parámetro del post
    updateURL(postId);
  };

  const handleBackToList = () => {
    setSelectedPost(null);
    trackInteraction('back_to_list', 'navigation');
    // Limpiar parámetros de URL
    updateURL(null);
  };

  const handleCategoryFilter = (category) => {
    setSelectedCategory(category);
    trackInteraction('category_filter', 'blog_filter', {
      category: category
    });
  };

  const handleCreatePost = () => {
    setShowCreateForm(true);
    trackInteraction('create_post_click', 'blog_action');
  };

  const handleCloseForm = () => {
    setShowCreateForm(false);
  };

  const handlePostCreated = async () => {
    // Actualizar la lista de posts después de crear uno nuevo
    setShowCreateForm(false);
    await loadPosts();
    
    // Mostrar notificación de éxito
    setNotification({
      type: 'success',
      message: 'Post creado exitosamente'
    });
    
    // Ocultar notificación después de 3 segundos
    setTimeout(() => {
      setNotification(null);
    }, 3000);
    
    trackInteraction('post_created', 'blog_action');
  };

  // Cargar posts desde Firebase
  const loadPosts = async () => {
    try {
      setLoading(true);
      setError(null);
      const posts = await blogService.getAllPublishedPosts();
      
      // Formatear los posts para que coincidan con el formato esperado
      const formattedPosts = posts.map(post => ({
        ...post,
        date: post.createdAt ? new Date(post.createdAt.seconds * 1000).toLocaleDateString('es-ES', {
          day: 'numeric',
          month: 'short',
          year: 'numeric'
        }) : 'Fecha desconocida'
      }));
      
      setBlogPosts(formattedPosts);
    } catch (err) {
      console.error('Error loading posts:', err);
      setError('Error al cargar los posts');
    } finally {
      setLoading(false);
    }
  };
  
  // Función para actualizar la URL con parámetros
  const updateURL = (postId) => {
    const url = new URL(window.location);
    if (postId) {
      url.searchParams.set('post', postId);
    } else {
      url.searchParams.delete('post');
    }
    window.history.pushState({}, '', url);
  };

  // Función para leer parámetros de URL
  const getPostFromURL = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('post');
  };

  // Manejar navegación del navegador (botón atrás/adelante)
  useEffect(() => {
    const handlePopState = () => {
      const postId = getPostFromURL();
      if (postId && blogPosts.length > 0) {
        const post = blogPosts.find(p => p.id === postId);
        if (post) {
          setSelectedPost(post);
        }
      } else {
        setSelectedPost(null);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [blogPosts]);

  // Verificar si hay un post en la URL al cargar posts
  useEffect(() => {
    if (blogPosts.length > 0) {
      const postId = getPostFromURL();
      if (postId) {
        const post = blogPosts.find(p => p.id === postId);
        if (post) {
          setSelectedPost(post);
        }
      }
    }
  }, [blogPosts]);

  // Cargar posts al montar el componente
  useEffect(() => {
    loadPosts();
  }, []);
  

  
  // Función para cerrar notificación
  const closeNotification = () => {
    setNotification(null);
  };

  // Función para compartir post
  const handleSharePost = async (post) => {
    const url = new URL(window.location);
    url.searchParams.set('post', post.id);
    const shareUrl = url.toString();
    
    try {
      if (navigator.share) {
        // Usar Web Share API si está disponible
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: shareUrl
        });
        trackInteraction('post_shared', 'share_api', { post_id: post.id });
      } else {
        // Fallback: copiar al portapapeles
        await navigator.clipboard.writeText(shareUrl);
        setNotification({
          type: 'success',
          message: 'Link copiado al portapapeles'
        });
        setTimeout(() => setNotification(null), 3000);
        trackInteraction('post_shared', 'clipboard', { post_id: post.id });
      }
    } catch (err) {
      console.log('Error sharing:', err);
      // Si falla, mostrar el link para copiarlo manualmente
      setNotification({
        type: 'info',
        message: `Link del post: ${shareUrl}`
      });
      setTimeout(() => setNotification(null), 5000);
    }
  };

  // Filtrar posts por categoría
  const categories = ['Todos', ...new Set(blogPosts.map(post => post.category))];
  
  const filteredPosts = selectedCategory === 'Todos' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  // Si hay un post seleccionado, mostrar vista de detalle
  if (selectedPost) {
    return (
      <div className="blog-container">
        <div className="blog-post-detail">
          <div className="container">
            <div className="post-detail-header">
              <button className="back-button" onClick={handleBackToList}>
                ← Volver al Blog
              </button>
              <div className="post-detail-meta">
                <span className="post-category">{selectedPost.category}</span>
                <div className="post-info">
                  <span className="post-date">{selectedPost.date}</span>
                  <span className="post-divider">•</span>
                  <span className="post-read-time">{selectedPost.readTime}</span>
                </div>
              </div>
            </div>
            
            <div className="post-detail-content">
              <div className="post-hero">
                <div className="post-hero-image">
                  <span className="post-emoji-large">{selectedPost.image}</span>
                </div>
                <h1 className="post-detail-title">{selectedPost.title}</h1>
                <div className="post-author-info">
                  <span className="author-name">Por {selectedPost.author}</span>
                </div>
              </div>
              
              <div className="post-body">
                <div className="post-excerpt-large">
                  {selectedPost.excerpt}
                </div>
                <div 
                  className="post-content"
                  dangerouslySetInnerHTML={{ __html: selectedPost.content }}
                />
              </div>
              
              <div className="post-detail-footer">
                <div className="post-tags">
                  {selectedPost.tags.map(tag => (
                    <span key={tag} className="post-tag">{tag}</span>
                  ))}
                </div>
                <div className="post-actions">
                  <button 
                    className="share-button"
                    onClick={() => handleSharePost(selectedPost)}
                  >
                    📤 Compartir
                  </button>
                  <button className="save-button">
                    📎 Guardar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Mostrar loading mientras se cargan los posts
  if (loading) {
    return (
      <div className="blog-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Cargando posts...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="blog-container">
      {/* Notificación */}
      {notification && (
        <div className={`notification ${notification.type}`}>
          <span>{notification.message}</span>
          <button className="notification-close" onClick={closeNotification}>×</button>
        </div>
      )}
      
      {/* Mensaje de error */}
      {error && (
        <div className="error-banner">
          <span>⚠️ {error}</span>
          <button onClick={loadPosts} className="retry-button">Reintentar</button>
        </div>
      )}
      <div className="blog-header">
        <div className="container">
          <div className="blog-header-content">
            <div className="blog-header-text">
              <h1 className="blog-title">Blog ExpertMind</h1>
              <p className="blog-subtitle">
                Insights, casos de éxito y guías prácticas sobre integración empresarial e inteligencia artificial
              </p>
            </div>
            <div className="blog-header-actions">
              {isAuthenticated && (
                <button className="create-blog-button" onClick={handleCreatePost}>
                  <span className="button-icon">✏️</span>
                  Crear Blog
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="blog-content">
        <div className="container">
          {/* Filtros de categoría */}
          <div className="blog-filters">
            <div className="filter-buttons">
              {categories.map(category => (
                <button
                  key={category}
                  className={`filter-button ${selectedCategory === category ? 'active' : ''}`}
                  onClick={() => handleCategoryFilter(category)}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          <div className="blog-posts">
            {filteredPosts.map(post => (
              <article 
                key={post.id} 
                className="blog-post-card"
                onClick={() => handlePostClick(post.id, post.title)}
              >
                <div className="post-header">
                  <div className="post-image">
                    <span className="post-emoji">{post.image}</span>
                  </div>
                  <div className="post-meta">
                    <span className="post-category">{post.category}</span>
                    <div className="post-info">
                      <span className="post-date">{post.date}</span>
                      <span className="post-divider">•</span>
                      <span className="post-read-time">{post.readTime}</span>
                    </div>
                  </div>
                </div>
                
                <div className="post-content">
                  <h2 className="post-title">{post.title}</h2>
                  <p className="post-excerpt">{post.excerpt}</p>
                </div>
                
                <div className="post-footer">
                  <div className="post-author">
                    <span className="author-name">Por {post.author}</span>
                  </div>
                  <div className="post-tags">
                    {post.tags.map(tag => (
                      <span key={tag} className="post-tag">{tag}</span>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
      
      {/* Formulario de creación de post */}
      {showCreateForm && (
        <BlogPostForm
          onClose={handleCloseForm}
          onPostCreated={handlePostCreated}
        />
      )}
    </div>
  );
};

export default Blog;